import {
  Alert,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Radio,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type {
  CoderProbeStatus,
  CoderProbeStatusResponse,
  CoderProviderResponse,
  InstallHint,
} from "./api";

// Презентационные части секции «Система-кодер» (ADR-0074), вынесенные
// из `CoderSystemSection.tsx` ради лимита длины файла: одна radio-строка
// каталога, список команд установки и форма «Указать путь», а также
// recovery-flow для not-found выбранного кодера, который их обе
// использует.

// Подбираем bg-цвет бейджа под probe-статус. healthy — зелёный,
// not_found — серый, остальное (unhealthy) — жёлтый.
function badgeColor(status: CoderProbeStatus): string {
  if (status === "healthy") {
    return "green";
  }
  if (status === "not_found") {
    return "gray";
  }
  return "yellow";
}

/**
 * Одна строка radio каталога кодеров. Бейдж probe-статуса и
 * resolved-path рисуются только при `showStatus` — без сведений о
 * бинарях раннера (`runner_available=false`) их не показываем.
 */
export function ProviderRow(props: {
  readonly provider: CoderProviderResponse;
  readonly probe: CoderProbeStatusResponse | undefined;
  readonly showStatus: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { provider, probe, showStatus } = props;
  const status = probe?.status ?? "not_found";

  const badgeLabel = (): string => {
    if (status === "healthy") {
      return t("connections.coderSystem.statusHealthy", {
        version: probe?.version ?? "",
      });
    }
    if (status === "not_found") {
      return t("connections.coderSystem.statusNotFound");
    }
    return t("connections.coderSystem.statusUnhealthy");
  };

  return (
    <Stack gap={4}>
      <Group gap="sm">
        <Radio value={provider.coder_kind} label={provider.coder_kind} />
        {showStatus ? (
          <Badge color={badgeColor(status)}>{badgeLabel()}</Badge>
        ) : null}
      </Group>
      {showStatus &&
      probe?.resolved_path !== null &&
      probe?.resolved_path !== undefined ? (
        <Text size="sm" c="dimmed">
          {t("connections.coderSystem.resolvedPath", {
            path: probe.resolved_path,
          })}
        </Text>
      ) : null}
    </Stack>
  );
}

/** Список официальных команд установки кодера плюс ссылка на доки. */
export function InstallHintCommands(props: {
  readonly installHint: InstallHint;
}): ReactElement {
  const { t } = useTranslation();
  const { installHint } = props;

  return (
    <Stack gap="sm">
      {installHint.commands.map((cmd) => (
        <Group key={cmd.platform} gap="sm">
          <Text size="sm" fw={500}>
            {cmd.platform}
          </Text>
          <Code>{cmd.command}</Code>
          <CopyButton value={cmd.command}>
            {({ copied, copy }) => (
              <Button size="xs" variant="default" onClick={copy}>
                {copied
                  ? t("connections.coderSystem.copied")
                  : t("connections.coderSystem.copy")}
              </Button>
            )}
          </CopyButton>
        </Group>
      ))}
      <Text size="sm">
        <a href={installHint.url} target="_blank" rel="noreferrer">
          {t("connections.coderSystem.installDocs")}
        </a>
      </Text>
    </Stack>
  );
}

/**
 * Поле «Указать путь» — сохраняется без проверки бинаря, trim/NULL
 * делает backend. Используется и в recovery-flow, и когда сведений о
 * бинарях раннера нет вовсе.
 */
export function OverridePathForm(props: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSave: () => void;
  readonly pending: boolean;
  readonly disabled?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { value, onChange, onSave, pending, disabled = false } = props;

  return (
    <Stack gap="sm">
      <TextInput
        label={t("connections.coderSystem.pathLabel")}
        description={t("connections.coderSystem.pathHint")}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      />
      <Group>
        <Button onClick={onSave} loading={pending} disabled={disabled}>
          {t("connections.coderSystem.savePathButton")}
        </Button>
      </Group>
    </Stack>
  );
}

// Опции recovery-flow для not-found выбранного кодера (radio).
// `auto` («Поставить за меня») пока не реализуется (требует
// opt-in vendor-signed) — рисуем disabled.
export type RecoveryOption = "hint" | "path";

/**
 * Recovery-flow для not-found выбранного кодера: install-hint или путь.
 *
 * Переключатель «команда / путь» только меняет вид и ничего не
 * сохраняет, поэтому `disabled` гасит лишь форму пути.
 */
export function RecoveryFlow(props: {
  readonly installHint: InstallHint;
  readonly recovery: RecoveryOption;
  readonly onRecoveryChange: (value: RecoveryOption) => void;
  readonly pathValue: string;
  readonly onPathChange: (value: string) => void;
  readonly onSavePath: () => void;
  readonly savePending: boolean;
  readonly disabled?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const {
    installHint,
    recovery,
    onRecoveryChange,
    pathValue,
    onPathChange,
    onSavePath,
    savePending,
    disabled = false,
  } = props;

  return (
    <Stack gap="md">
      <Alert color="gray" title={t("connections.coderSystem.recoveryHeading")}>
        {t("connections.coderSystem.recoveryIntro")}
      </Alert>

      <Radio.Group
        value={recovery}
        onChange={(value) => {
          onRecoveryChange(value);
        }}
        label={t("connections.coderSystem.recoveryActionLabel")}
      >
        <Stack gap="sm">
          <Radio
            value="hint"
            label={t("connections.coderSystem.recoveryHint")}
          />
          <Radio
            value="path"
            label={t("connections.coderSystem.recoveryPath")}
          />
          <Radio
            value="auto"
            disabled
            label={t("connections.coderSystem.recoveryAuto")}
          />
        </Stack>
      </Radio.Group>

      {recovery === "hint" ? (
        <InstallHintCommands installHint={installHint} />
      ) : null}

      {recovery === "path" ? (
        <OverridePathForm
          value={pathValue}
          onChange={onPathChange}
          onSave={onSavePath}
          pending={savePending}
          disabled={disabled}
        />
      ) : null}
    </Stack>
  );
}
